// src/components/modal/Modal.jsx
import React from 'react';
import styled from 'styled-components';

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 11000; /* выше шапки */
  display: grid;
  place-items: center;
  padding: 24px;

  background: rgba(0,0,0,0.5);
  opacity: ${p => (p.$entered ? 1 : 0)};
  transition: opacity .25s ease;
`;

const Panel = styled.div`
  width: min(680px, 92vw);
  max-height: calc(100vh - 48px);
  overflow: auto;

  position: relative;
  padding: 24px;
  border-radius: 16px;
  background: var(--card);
  color: var(--fg);
  border: 1px solid var(--bdr);
  box-shadow: var(--shadow), 0 30px 80px rgba(0,0,0,.35);

  /* лёгкий эффект стекла */
  backdrop-filter: blur(8px) saturate(160%);
  -webkit-backdrop-filter: blur(8px) saturate(160%);

  transform: translateY(${p => (p.$entered ? '0' : '14px')}) scale(${p => (p.$entered ? '1' : '.985')});
  opacity: ${p => (p.$entered ? 1 : 0.92)};
  transition:
    transform .25s cubic-bezier(.2,.8,.2,1),
    opacity .25s ease;
  will-change: transform, opacity;
`;

const Close = styled.button`
  position:absolute; top:10px; right:10px;
  width:36px; height:36px; border-radius:10px;
  background:transparent; border:1px solid var(--bdr);
  color:inherit; font-size:20px; line-height:1;
  cursor:pointer;
  display:flex; align-items:center; justify-content:center;
  transition: transform .12s ease, background .12s ease;
  &:hover{ background: rgba(0,0,0,.05); transform: translateY(-1px); }
  &:active{ transform: translateY(0); }
`;

/**
 * Плавная модалка:
 *  - open: boolean
 *  - onClose: () => void
 *  - closeOnBackdrop?: boolean (default: true)
 *  - closeOnEsc?: boolean (default: true)
 */
export default function Modal({
  open,
  onClose,
  closeOnBackdrop = true,
  closeOnEsc = true,
  children,
}) {
  const [mounted, setMounted] = React.useState(!!open);
  const [entered, setEntered] = React.useState(!!open);

  const prevOverflowRef = React.useRef('');
  const backRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const raf1 = React.useRef(0);
  const raf2 = React.useRef(0);

  React.useEffect(() => {
    if (open) {
      setMounted(true);
      setEntered(false); // стартовые стили (невидимо/смещено)

      // блокируем скролл страницы
      prevOverflowRef.current = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';

      // Двойной rAF + рефлоу гарантируют, что стартовые стили применятся,
      // и переход на entered=true будет анимирован
      raf1.current = requestAnimationFrame(() => {
        // форсируем рефлоу
        backRef.current?.getBoundingClientRect();
        panelRef.current?.getBoundingClientRect();

        raf2.current = requestAnimationFrame(() => {
          setEntered(true); // поехали! плавный enter
        });
      });

      return () => {
        cancelAnimationFrame(raf1.current);
        cancelAnimationFrame(raf2.current);
      };
    } else {
      // Запускаем плавный exit
      setEntered(false);
      const t = setTimeout(() => {
        setMounted(false);
        document.documentElement.style.overflow = prevOverflowRef.current || '';
      }, 260); // немного больше transition (250ms)
      return () => clearTimeout(t);
    }
  }, [open]);

  // Закрытие по Esc
  React.useEffect(() => {
    if (!open || !closeOnEsc) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, closeOnEsc, onClose]);

  if (!mounted) return null;

  const onBackdropClick = () => closeOnBackdrop && onClose?.();

  return (
    <Backdrop
      ref={backRef}
      role="dialog"
      aria-modal="true"
      $entered={entered}
      onMouseDown={onBackdropClick}
    >
      <Panel
        ref={panelRef}
        $entered={entered}
        onMouseDown={(e) => e.stopPropagation()} // не закрываем по клику внутри
      >
        <Close onClick={onClose} aria-label="Close">×</Close>
        {children}
      </Panel>
    </Backdrop>
  );
}
